import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

// Compact multi-select for the "Doing Business In" table cell.
//
// A native <select multiple> is an inline list-box that needs Ctrl/Cmd+click, and a native single
// <select> can't do multi-select. This control instead shows a Business-Line-style trigger and a
// click-to-toggle checkbox menu. The menu is rendered through a portal into <body> and positioned
// with fixed coordinates, so it is NOT clipped by the account table's horizontal-scroll overflow
// (the reason the custom in-table dropdown was getting cut off).
//
// `value` is the comma-separated full-name string stored on the account (e.g. "New York, New
// Jersey"); `onChange` is called with the updated comma-separated string, so the value contract
// with the parent (and everything downstream) is unchanged.
const DoingBusinessInSelect = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const selectedNames = value
    ? value
        .split(", ")
        .map(s => s.trim())
        .filter(Boolean)
    : [];
  const selectedSet = new Set(selectedNames);
  // Keep only recognised state names when the admin edits, so any legacy/dirty token (which has no
  // checkbox to un-tick) is cleaned up on the next change instead of sticking around forever.
  const validLabels = new Set(options.map(o => o.label));
  const validSelectedNames = selectedNames.filter(n => validLabels.has(n));

  const openMenu = () => {
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuStyle({
      position: "fixed",
      top: `${rect.bottom}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
    });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const closeIfOutside = e => {
      if (
        !triggerRef.current?.contains(e.target) &&
        !menuRef.current?.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    // Close on OUTSIDE scroll / resize so the fixed menu never drifts away from its trigger, but
    // ignore scrolling that happens inside the menu's own option list (otherwise scrolling the
    // list would close it).
    const onScroll = e => {
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onResize = () => setOpen(false);
    document.addEventListener("mousedown", closeIfOutside);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("mousedown", closeIfOutside);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  const toggle = label => {
    const next = validSelectedNames.includes(label)
      ? validSelectedNames.filter(n => n !== label)
      : [...validSelectedNames, label];
    onChange(next.join(", "));
  };

  return (
    <div className="dbi-select" ref={triggerRef}>
      <button
        type="button"
        className="dbi-select__trigger"
        onClick={() => (open ? setOpen(false) : openMenu())}
      >
        <span className="dbi-select__value">{value || "Select States"}</span>
      </button>
      {open &&
        menuStyle &&
        ReactDOM.createPortal(
          <div ref={menuRef} className="dbi-select__menu" style={menuStyle}>
            {options.map(opt => (
              <label key={opt.value} className="dbi-select__option">
                <input
                  type="checkbox"
                  checked={selectedSet.has(opt.label)}
                  onChange={() => toggle(opt.label)}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default DoingBusinessInSelect;
